import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import { AuthProvider } from '@/contexts/AuthContext';
import { StaffAuthProvider } from '@/contexts/StaffAuthContext';
import { PosAuthProvider } from '@/contexts/PosAuthContext';
import { PortalAuthProvider } from '@/contexts/PortalAuthContext';
import { ProtectedCustomerRoute } from '@/components/customer/ProtectedCustomerRoute';
import { ProtectedStaffRoute } from '@/components/staff/ProtectedStaffRoute';
import { ProtectedPosRoute } from '@/components/pos/ProtectedPosRoute';
import { ManifestSwitcher } from '@/components/pos/ManifestSwitcher';
import AdminLayout from '@/components/staff/AdminLayout';

import Marketing from '@/pages/Marketing';
import Login from '@/pages/customer/Login';
import Register from '@/pages/customer/Register';
import ResetWithCode from '@/pages/customer/ResetWithCode';
import Account from '@/pages/customer/Account';
import History from '@/pages/customer/History';
import Profile from '@/pages/customer/Profile';
import MockPayment from '@/pages/customer/MockPayment';

import Privacy from '@/pages/legal/Privacy';
import Terms from '@/pages/legal/Terms';
import Refunds from '@/pages/legal/Refunds';
import Support from '@/pages/legal/Support';
import DeleteAccount from '@/pages/legal/DeleteAccount';
import PaymentTerms from '@/pages/legal/PaymentTerms';
import About from '@/pages/legal/About';
import Allergy from '@/pages/legal/Allergy';

import StaffLogin from '@/pages/staff/StaffLogin';
import Dashboard from '@/pages/staff/Dashboard';
import Customers from '@/pages/staff/Customers';
import CustomerProfile from '@/pages/staff/CustomerProfile';
import Subscriptions from '@/pages/staff/Subscriptions';
import SubscriptionDetail from '@/pages/staff/SubscriptionDetail';
import CancelledSubscriptions from '@/pages/staff/CancelledSubscriptions';
import QRScanner from '@/pages/staff/QRScanner';
import Redemptions from '@/pages/staff/Redemptions';
import PendingPayments from '@/pages/staff/PendingPayments';
import Plans from '@/pages/staff/Plans';
import Payments from '@/pages/staff/Payments';
import Notifications from '@/pages/staff/Notifications';
import Reports from '@/pages/staff/Reports';
import StaffPage from '@/pages/staff/StaffPage';
import StaffProfile from '@/pages/staff/StaffProfile';
import Scheduling from '@/pages/staff/Scheduling';
import Checklists from '@/pages/staff/Checklists';
import Timesheet from '@/pages/staff/Timesheet';
import Settings from '@/pages/staff/Settings';

import TimeClock from '@/pages/timeclock/TimeClock';

import PosLogin from '@/pages/pos/PosLogin';
import PosHome from '@/pages/pos/PosHome';
import PosScanner from '@/pages/pos/PosScanner';
import PosSearch from '@/pages/pos/PosSearch';
import PosPendingPayments from '@/pages/pos/PosPendingPayments';

import { ProtectedPortalRoute } from '@/components/portal/ProtectedPortalRoute';
import PortalLayout from '@/components/portal/PortalLayout';
import PortalLogin from '@/pages/portal/PortalLogin';
import PortalHome from '@/pages/portal/PortalHome';
import PortalSchedule from '@/pages/portal/PortalSchedule';
import PortalDocuments from '@/pages/portal/PortalDocuments';
import PortalTasks from '@/pages/portal/PortalTasks';
import PortalChecklists from '@/pages/portal/PortalChecklists';
import PortalProfile from '@/pages/portal/PortalProfile';

function App() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#0A0A0A' });
      } catch {
        // Not fatal — status bar just keeps its OS default styling.
      }
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch {
        // Splash auto-hides after launchShowDuration anyway (capacitor.config.json).
      }
    })();
  }, []);

  return (
    <AuthProvider>
      <StaffAuthProvider>
        <PosAuthProvider>
        <PortalAuthProvider>
        <BrowserRouter>
          <ManifestSwitcher />
          <Toaster richColors position="top-center" />
          <Routes>
            <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/cxlogin" replace /> : <Marketing />} />

            <Route path="/welcome" element={<Marketing />} />

            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/support" element={<Support />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/payment-terms" element={<PaymentTerms />} />
            <Route path="/about" element={<About />} />
            <Route path="/allergy" element={<Allergy />} />

            <Route path="/login" element={<Navigate to="/cxlogin" replace />} />
            <Route path="/cxlogin" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-with-code" element={<ResetWithCode />} />
            <Route path="/account" element={<ProtectedCustomerRoute><Account /></ProtectedCustomerRoute>} />
            <Route path="/history" element={<ProtectedCustomerRoute><History /></ProtectedCustomerRoute>} />
            <Route path="/profile" element={<ProtectedCustomerRoute><Profile /></ProtectedCustomerRoute>} />
            <Route path="/pay/:paymentId" element={<ProtectedCustomerRoute><MockPayment /></ProtectedCustomerRoute>} />

            <Route path="/staff/login" element={<StaffLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedStaffRoute>
                  <AdminLayout />
                </ProtectedStaffRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerProfile />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="subscriptions/cancelled" element={<CancelledSubscriptions />} />
              <Route path="subscriptions/:id" element={<SubscriptionDetail />} />
              <Route path="scanner" element={<QRScanner />} />
              <Route path="redemptions" element={<Redemptions />} />
              <Route path="pending-payments" element={<PendingPayments />} />
              <Route path="plans" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Plans /></ProtectedStaffRoute>} />
              <Route path="payments" element={<Payments />} />
              <Route path="notifications" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Notifications /></ProtectedStaffRoute>} />
              <Route path="reports" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Reports /></ProtectedStaffRoute>} />
              <Route path="staff" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><StaffPage /></ProtectedStaffRoute>} />
              <Route path="staff/:id" element={<ProtectedStaffRoute roles={['ADMIN']}><StaffProfile /></ProtectedStaffRoute>} />
              <Route path="scheduling" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Scheduling /></ProtectedStaffRoute>} />
              <Route path="checklists" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Checklists /></ProtectedStaffRoute>} />
              <Route path="timesheet" element={<ProtectedStaffRoute roles={['ADMIN', 'MANAGER']}><Timesheet /></ProtectedStaffRoute>} />
              <Route path="settings" element={<ProtectedStaffRoute roles={['ADMIN']}><Settings /></ProtectedStaffRoute>} />
            </Route>

            <Route path="/timeclock" element={<TimeClock />} />

            <Route path="/pos" element={<PosLogin />} />
            <Route path="/pos/home" element={<ProtectedPosRoute><PosHome /></ProtectedPosRoute>} />
            <Route path="/pos/scanner" element={<ProtectedPosRoute><PosScanner /></ProtectedPosRoute>} />
            <Route path="/pos/search" element={<ProtectedPosRoute><PosSearch /></ProtectedPosRoute>} />
            <Route path="/pos/pending-payments" element={<ProtectedPosRoute><PosPendingPayments /></ProtectedPosRoute>} />

            <Route path="/portal/login" element={<PortalLogin />} />
            <Route
              path="/portal"
              element={
                <ProtectedPortalRoute>
                  <PortalLayout />
                </ProtectedPortalRoute>
              }
            >
              <Route index element={<PortalHome />} />
              <Route path="schedule" element={<PortalSchedule />} />
              <Route path="documents" element={<PortalDocuments />} />
              <Route path="tasks" element={<PortalTasks />} />
              <Route path="checklists" element={<PortalChecklists />} />
              <Route path="profile" element={<PortalProfile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </PortalAuthProvider>
        </PosAuthProvider>
      </StaffAuthProvider>
    </AuthProvider>
  );
}

export default App;
